import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { Themed } from "@components/atoms/Themed"
import { ScrollReveal } from "@components/motion/Transitioned"
import { useGSAP } from "@gsap/react"
import DrawSVGPlugin from "gsap/dist/DrawSVGPlugin"
import { cn } from "@utils/tw"
import { defineBlock, InnerBlocks, useInnerBlocks } from "eddev/blocks"
import slugify from "slugify"
import { useIntersectionObserver } from "usehooks-ts"
import gsap from "gsap"
import { useRef } from "react"

if (env.client) {
  gsap.registerPlugin(useGSAP)
  gsap.registerPlugin(DrawSVGPlugin)
  gsap.config({
    nullTargetWarn: false,
  })
}

export const meta: BlockMeta = {
  title: "Sticky Section",
  icon: "block-default",
  tags: ["#page"],
}

export default defineBlock("content/sticky-section", (props) => {
  const outerRef = useRef<HTMLDivElement>(null)
  const { isIntersecting, ref } = useIntersectionObserver({
    root: null,
    rootMargin: "0px",
    threshold: 0.1,
    freezeOnceVisible: true,
  })

  useGSAP(
    () => {
      const tl = gsap.timeline({ paused: true })

      tl.add(
        gsap.fromTo(".image-path rect", { drawSVG: "0%" }, { drawSVG: "97.5%", duration: 1.5, ease: "sine.out" }),
        0,
      )

      tl.add(gsap.fromTo(".media-asset", { opacity: 0 }, { opacity: 1, duration: 1.5, ease: "sine.out" }), 0.5)

      if (isIntersecting) {
        tl.play()
      }

      return () => {
        tl?.revert().kill()
      }
    },
    {
      scope: outerRef,
      dependencies: [isIntersecting],
    },
  )

  return (
    <div className="w-full" ref={outerRef}>
      <ScrollReveal>
        <div ref={ref} className="w-full">
          <a className="block" id={`section-${slugify(props.title || "")}`} />
          <Themed
            theme={props.pageTheme || "none"}
            className="w-full md:min-h-[100dvh] pt-10 pb-10 md:pt-11 md:pb-[calc(var(--spacing-11)+var(--spacing-stickyMediaH))] themed-fg relative"
          >
            {/* Background color */}
            <div className="themed-bg absolute left-0 top-0 w-full h-full" />

            {/* Content */}
            <div className={cn("w-full max-w-grid mx-auto")}>
              <div
                className={cn(
                  "hidden md:block -mb-stickyMediaH w-stickyMediaW h-stickyMediaH shrink-0 sticky top-11",
                  props.alternate ? "left-[calc(100%-var(--spacing-stickyMediaW))]" : "left-0",
                )}
              >
                <div className="w-full h-full relative">
                  {env.admin && !props.image?.mediaItemUrl && !props.video?.mediaItemUrl && (
                    <div className="w-full h-full bg-grey flex items-center justify-center type-label-m text-black rounded-[2.3em] overflow-hidden">
                      Select media
                    </div>
                  )}
                  {props.image && !props.video && (
                    <ResponsiveImage
                      {...props.image}
                      className="media-asset w-full h-full rounded-[2.3em] overflow-hidden"
                      objectFit="cover"
                    />
                  )}
                  {!props.image && props.video?.mediaItemUrl && (
                    <video
                      src={props.video.mediaItemUrl}
                      className="media-asset w-full h-full object-cover object-center rounded-[2.3em] overflow-hidden"
                      playsInline
                      autoPlay
                      muted
                      loop
                    />
                  )}

                  <svg
                    className="image-path w-[calc(100%+2px)] h-[calc(100%+2px)] absolute left-[-1px] top-[-1px] pointer-events-none"
                    viewBox="0 0 592 421"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="1" y="1" width="590" height="419" rx="31" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
              </div>

              <div
                className={cn(
                  "w-full flex flex-col md:flex-row md:flex-nowrap gap-7 md:gap-10",
                  props.alternate && "md:flex-row-reverse",
                )}
              >
                <div
                  className={cn(
                    "w-full aspect-[592/420] relative md:aspect-auto md:w-stickyMediaW md:h-stickyMediaH md:invisible md:shrink-0 md:sticky md:top-0 md:left-0",
                  )}
                >
                  <div className="w-full h-full relative">
                    {env.admin && !props.image?.mediaItemUrl && !props.video?.mediaItemUrl && (
                      <div className="w-full h-full bg-grey flex items-center justify-center type-label-m text-black">
                        Select media
                      </div>
                    )}
                    {props.image && !props.video && (
                      <ResponsiveImage
                        {...props.image}
                        className="w-full h-full rounded-lg overflow-hidden media-asset"
                        objectFit="cover"
                      />
                    )}
                    {!props.image && props.video?.mediaItemUrl && (
                      <video
                        src={props.video.mediaItemUrl}
                        className="w-full h-full object-cover object-center rounded-lg overflow-hidden media-asset"
                        playsInline
                        autoPlay
                        muted
                        loop
                      />
                    )}
                  </div>
                  <svg
                    className="image-path w-[calc(100%+2px)] h-[calc(100%+2px)] absolute left-[-1px] top-[-1px] pointer-events-none"
                    viewBox="0 0 592 421"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="1" y="1" width="590" height="419" rx="31" stroke="currentColor" strokeWidth="2" />
                  </svg>
                </div>
                <div className={cn("w-full")} data-transition="scale">
                  {!props.title && env.admin && (
                    <p className="mb-5 p-4 bg-grey-light rounded-md text-black">Enter a title (in the sidebar)</p>
                  )}
                  <p className="type-title-m md:type-title-l mb-5 block">{props.title}</p>
                  <div className="[&_h2]:!type-title-s md:[&_h2]:!type-title-m [&_h2]:!mt-0 [&_h2]:!pt-3 [&_h2]:!mb-5 [&_p]:!mt-0 [&_p]:!mb-5 [&_p.is-style-heading]:!mb-9 [&_li]:!type-body-xl [&_li]:!my-0 [&_a]:!underline">
                    <InnerBlocks
                      allowedBlocks={["#inline", "content/expandable"]}
                      adminClassName="flex flex-col gap-4"
                      defaultBlocks={[
                        ["core/paragraph", { placeholder: "Enter your text here...", className: "is-style-heading" }],
                        ["core/heading", { placeholder: "Enter a heading...", level: 2 }],
                        [
                          "core/paragraph",
                          { placeholder: "Enter your text here...", className: "is-style-body-large" },
                        ],
                        ["content/expandable", {}],
                      ]}
                      orientation="vertical"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Themed>
        </div>
      </ScrollReveal>
    </div>
  )
})
